import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  ToastNotification,
  ToastNotificationVariant
} from '@cmhc-schl/hearth-ui';
import { Box, Typography } from '@mui/material';
import { getInsuredApprovedlenderName } from 'Feature/V2/HbtPortfolioManagement/utils/GetLenderName';
import { MemoizedHbtDataTableCell } from 'Feature/V2/Shared/MemoizedHbtDataTableCell';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import styles from '../styles.module.scss';
import { CMHCLowRatioCharacteristicsSectionProps } from '../types';
import { TableRow } from './tableRow';
import {
  dwellingTypes,
  loanPurposeTypes,
  lowRatioCharacteristicsAgeTypes,
  lowRatioCharacteristicsApplicationRiskTypes,
  lowRatioCharacteristicsResultTypes,
  tenureTypes
} from './utils/statusCodes';
import CopyContentToClipBoard from './CopyContentToClipBoard';
import { useLocation } from 'react-router-dom';
import { getSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getSearchParams';
import { handleScrollTo } from '../index';

export const CMHCLowRatioCharacteristicsSection = ({
  isCollapsed,
  onToggle,
  cmhcLowRatioData,
  options
}: CMHCLowRatioCharacteristicsSectionProps) => {
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const [showToaster, setShowToaster] = useState(false);
  const [toaster, setToasterMsg] = useState({
    icon: 'icon_check_circle',
    message: {
      header: t('Copied'),
      subHeader: String(' ')
    }
  });

  const location = useLocation();
  const { dataType } = getSearchParams(location.search);
  const cmhcLowRatioCharacteristicsRef = useRef(null);
  const initial = '0';
  const subInitial = '1';

  useEffect(() => {
    if (!sessionStorage.getItem('searchLoanDetail')) return;
    const shouldScrollToCmhcLowRatioAccountNumberSection =
      cmhcLowRatioData &&
      cmhcLowRatioCharacteristicsRef.current &&
      dataType === 'CMHC Low Ratio Account Number';
    if (shouldScrollToCmhcLowRatioAccountNumberSection) {
      handleScrollTo(cmhcLowRatioCharacteristicsRef);
    }
  }, [cmhcLowRatioData, cmhcLowRatioCharacteristicsRef.current]);

  const formatLowRatioAgeForValidationReview = (age?: string | number): string => {
    if (age === undefined || age === null) return '';
    const rawValue = typeof age === 'string' ? age.trim() : age;
    if (rawValue === '') return '';

    const parsedAge = Number(rawValue);
    if (!Number.isFinite(parsedAge)) return String(rawValue);

    const monthTranslationKey =
      Math.abs(parsedAge) === 1
        ? 'PI-Portfolio-Management-Request-Overview-Month'
        : 'PI-Portfolio-Management-Request-Overview-Months';

    const displayValue = Number.isInteger(parsedAge) ? parsedAge.toString() : parsedAge.toString();

    return `${displayValue} ${t(monthTranslationKey)}`.trim();
  };

  const isValidationReviewLayout = options.LayoutContainerType === 'validation review';

  const lowRatioAgeDisplay = isValidationReviewLayout
    ? formatLowRatioAgeForValidationReview(cmhcLowRatioData?.lowRatioAge)
    : lowRatioCharacteristicsAgeTypes(cmhcLowRatioData?.lowRatioAge ?? '', t);

  return (
    <>
      <Box className={styles.toasterNotification} ref={cmhcLowRatioCharacteristicsRef}>
        <ToastNotification
          dismissMode="autoAndActionable"
          variant={ToastNotificationVariant.INFO}
          open={showToaster}
          header={{ value: toaster.message.header }}
          subHeader={{ value: toaster.message.subHeader }}
          onClose={() => setShowToaster(false)}
          icon={toaster.icon}
        />
      </Box>
      <Box className={`${styles.cmhcLowRatioContainer} ${isCollapsed ? styles.collapsed : ''}`}>
        <Box className={styles.accordionContainer} onClick={onToggle}>
          <Typography variant="h6" className={styles.accordionTitle}>
            {t('CMHC-Low-Ratio-Characteristics')}
          </Typography>
          <Button
            type="button"
            className={styles.toggleButton}
            id={styles.toggleButton}
            name="lender"
            size={ButtonSize.SMALL}
            variant={ButtonVariant.SECONDARY}
            onClick={onToggle}
            leadingIcon={() => (
              <CmhcIcon
                className={styles.accordionIcon}
                size={CmhcIconSize.MEDIUM}
                type={isCollapsed ? 'icon_chevron_up' : 'icon_chevron_down'}
              />
            )}
            text={{
              value: ''
            }}
          />
        </Box>
        <Box className={styles.accordionBody}>
          <Box className={styles.tableContainer}>
            <Box>
              <TableRow
                leftTitle={t('Underwriting-Lender-Name')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(
                      getInsuredApprovedlenderName(
                        cmhcLowRatioData?.underwritingLenderName,
                        i18n.language
                      )
                    )}
                    clipboardContent={
                      getInsuredApprovedlenderName(
                        cmhcLowRatioData?.underwritingLenderName,
                        i18n.language
                      ) || ''
                    }
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(cmhcLowRatioData?.underwritingLenderName?.englishName)}
                      cellContent={
                        <span>
                          {getInsuredApprovedlenderName(
                            cmhcLowRatioData?.underwritingLenderName,
                            i18n.language
                          )}
                        </span>
                      }
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle=""
                rightValue={() => <></>}
                className={styles.hideCellTopBorder}
              />
              <TableRow
                leftTitle={t('Last-Refered-Date')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(cmhcLowRatioData?.lastReferedDate)}
                    clipboardContent={cmhcLowRatioData?.lastReferedDate || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(cmhcLowRatioData?.lastReferedDate)}
                      cellContent={<span>{cmhcLowRatioData?.lastReferedDate}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle={t('Account-Number')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(cmhcLowRatioData?.accountNumber)}
                    clipboardContent={cmhcLowRatioData?.accountNumber || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(cmhcLowRatioData?.accountNumber)}
                      cellContent={<span>{cmhcLowRatioData?.accountNumber}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
              />
              <TableRow
                leftTitle={t('Result')}
                leftValue={() => {
                  const resultCode = cmhcLowRatioData?.result ?? cmhcLowRatioData?.lowRatioResults;
                  return (
                    <CopyContentToClipBoard
                      onSuccess={() => setShowToaster(true)}
                      showClipButton={Boolean(resultCode)}
                      clipboardContent={resultCode || ''}
                    >
                      <MemoizedHbtDataTableCell
                        dataValue={
                          String(resultCode) === initial ? subInitial : String(resultCode ?? '')
                        }
                        cellContent={
                          <span>{lowRatioCharacteristicsResultTypes(resultCode ?? '', t)}</span>
                        }
                      />
                    </CopyContentToClipBoard>
                  );
                }}
                rightTitle={t('Low-Ratio-Age')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(cmhcLowRatioData?.lowRatioAge)}
                    clipboardContent={
                      isValidationReviewLayout
                        ? lowRatioAgeDisplay || cmhcLowRatioData?.lowRatioAge || ''
                        : cmhcLowRatioData?.lowRatioAge || ''
                    }
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(cmhcLowRatioData?.lowRatioAge)}
                      cellContent={<span>{lowRatioAgeDisplay}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
              />
              <TableRow
                leftTitle={t('Property-Risk')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(cmhcLowRatioData?.propertyRisk)}
                    clipboardContent={cmhcLowRatioData?.propertyRisk || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(cmhcLowRatioData?.propertyRisk)}
                      cellContent={<span>{cmhcLowRatioData?.propertyRisk}%</span>}
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle={t('Application-Risk')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(cmhcLowRatioData?.applicationRisk)}
                    clipboardContent={cmhcLowRatioData?.applicationRisk || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(cmhcLowRatioData?.applicationRisk)}
                      cellContent={
                        <span>
                          {lowRatioCharacteristicsApplicationRiskTypes(
                            cmhcLowRatioData?.applicationRisk ?? '',
                            t
                          )}
                        </span>
                      }
                    />
                  </CopyContentToClipBoard>
                )}
              />
              <TableRow
                leftTitle={t('Borrower-Risk')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(cmhcLowRatioData?.borrowerRisk)}
                    clipboardContent={cmhcLowRatioData?.borrowerRisk || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(cmhcLowRatioData?.borrowerRisk)}
                      cellContent={
                        <span>
                          {lowRatioCharacteristicsApplicationRiskTypes(
                            cmhcLowRatioData?.borrowerRisk ?? '',
                            t
                          )}
                        </span>
                      }
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle={t('Coborrower-Risk')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(cmhcLowRatioData?.coborrowerRisk)}
                    clipboardContent={cmhcLowRatioData?.coborrowerRisk || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(cmhcLowRatioData?.coborrowerRisk)}
                      cellContent={
                        <span>
                          {lowRatioCharacteristicsApplicationRiskTypes(
                            cmhcLowRatioData?.coborrowerRisk ?? '',
                            t
                          )}
                        </span>
                      }
                    />
                  </CopyContentToClipBoard>
                )}
              />
            </Box>
            <Box>
              {!options.isReducedDataSet && (
                <TableRow
                  leftTitle={t('Borrower-DoB')}
                  leftValue={() => (
                    <CopyContentToClipBoard
                      onSuccess={() => setShowToaster(true)}
                      showClipButton={Boolean(cmhcLowRatioData?.borrowerDoB)}
                      clipboardContent={cmhcLowRatioData?.borrowerDoB || ''}
                    >
                      <MemoizedHbtDataTableCell
                        dataValue={String(cmhcLowRatioData?.borrowerDoB)}
                        cellContent={<span>{cmhcLowRatioData?.borrowerDoB}</span>}
                      />
                    </CopyContentToClipBoard>
                  )}
                  rightTitle={t('Coborrower-DoB')}
                  rightValue={() => (
                    <CopyContentToClipBoard
                      onSuccess={() => setShowToaster(true)}
                      showClipButton={Boolean(cmhcLowRatioData?.coborrowerDoB)}
                      clipboardContent={cmhcLowRatioData?.coborrowerDoB || ''}
                    >
                      <MemoizedHbtDataTableCell
                        dataValue={String(cmhcLowRatioData?.coborrowerDoB)}
                        cellContent={<span>{cmhcLowRatioData?.coborrowerDoB}</span>}
                      />
                    </CopyContentToClipBoard>
                  )}
                />
              )}
              <TableRow
                leftTitle={t('Borrower-Credit-Score')}
                leftValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(cmhcLowRatioData?.borrowerCreditScore)}
                    clipboardContent={cmhcLowRatioData?.borrowerCreditScore || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(cmhcLowRatioData?.borrowerCreditScore)}
                      cellContent={<span>{cmhcLowRatioData?.borrowerCreditScore}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
                rightTitle={t('Coborrower-Credit-Score')}
                rightValue={() => (
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(cmhcLowRatioData?.coborrowerCreditScore)}
                    clipboardContent={cmhcLowRatioData?.coborrowerCreditScore || ''}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(cmhcLowRatioData?.coborrowerCreditScore)}
                      cellContent={<span>{cmhcLowRatioData?.coborrowerCreditScore}</span>}
                    />
                  </CopyContentToClipBoard>
                )}
              />
            </Box>
            {!options.isReducedDataSet && (
              <Box>
                <TableRow
                  leftTitle={t('Property-Address')}
                  leftValue={() => (
                    <CopyContentToClipBoard
                      onSuccess={() => setShowToaster(true)}
                      showClipButton={Boolean(cmhcLowRatioData?.propertyAddress)}
                      clipboardContent={cmhcLowRatioData?.propertyAddress || ''}
                    >
                      <MemoizedHbtDataTableCell
                        dataValue={String(cmhcLowRatioData?.propertyAddress)}
                        cellContent={<span>{cmhcLowRatioData?.propertyAddress}</span>}
                      />
                    </CopyContentToClipBoard>
                  )}
                  rightTitle={t('No-Of-Units')}
                  rightValue={() => (
                    <CopyContentToClipBoard
                      onSuccess={() => setShowToaster(true)}
                      showClipButton={Boolean(cmhcLowRatioData?.noOfUnits)}
                      clipboardContent={cmhcLowRatioData?.noOfUnits || ''}
                    >
                      <MemoizedHbtDataTableCell
                        dataValue={String(cmhcLowRatioData?.noOfUnits)}
                        cellContent={<span>{cmhcLowRatioData?.noOfUnits}</span>}
                      />
                    </CopyContentToClipBoard>
                  )}
                />
                <TableRow
                  leftTitle={t('Dwelling-Type')}
                  leftValue={() => (
                    <CopyContentToClipBoard
                      onSuccess={() => setShowToaster(true)}
                      showClipButton={Boolean(cmhcLowRatioData?.dwellingType)}
                      clipboardContent={cmhcLowRatioData?.dwellingType || ''}
                    >
                      <MemoizedHbtDataTableCell
                        dataValue={String(cmhcLowRatioData?.dwellingType)}
                        cellContent={
                          <span>{dwellingTypes(cmhcLowRatioData?.dwellingType ?? '', t)}</span>
                        }
                      />
                    </CopyContentToClipBoard>
                  )}
                  rightTitle={t('Tenure-Type')}
                  rightValue={() => (
                    <CopyContentToClipBoard
                      onSuccess={() => setShowToaster(true)}
                      showClipButton={Boolean(cmhcLowRatioData?.tenureType)}
                      clipboardContent={cmhcLowRatioData?.tenureType || ''}
                    >
                      <MemoizedHbtDataTableCell
                        dataValue={String(cmhcLowRatioData?.tenureType)}
                        cellContent={
                          <span>{tenureTypes(cmhcLowRatioData?.tenureType ?? '', t)}</span>
                        }
                      />
                    </CopyContentToClipBoard>
                  )}
                />
                <TableRow
                  leftTitle={t('Original-Lending-Value')}
                  leftValue={() => (
                    <CopyContentToClipBoard
                      onSuccess={() => setShowToaster(true)}
                      showClipButton={Boolean(cmhcLowRatioData?.originalLendingValue)}
                      clipboardContent={cmhcLowRatioData?.originalLendingValue || ''}
                    >
                      <MemoizedHbtDataTableCell
                        dataValue={String(cmhcLowRatioData?.originalLendingValue)}
                        cellContent={<span>{cmhcLowRatioData?.originalLendingValue}</span>}
                      />
                    </CopyContentToClipBoard>
                  )}
                  rightTitle={t('Original-Loan-Amount')}
                  rightValue={() => (
                    <CopyContentToClipBoard
                      onSuccess={() => setShowToaster(true)}
                      showClipButton={Boolean(cmhcLowRatioData?.originalLoanAmount)}
                      clipboardContent={cmhcLowRatioData?.originalLoanAmount || ''}
                    >
                      <MemoizedHbtDataTableCell
                        dataValue={String(cmhcLowRatioData?.originalLoanAmount)}
                        cellContent={<span>{cmhcLowRatioData?.originalLoanAmount}</span>}
                      />
                    </CopyContentToClipBoard>
                  )}
                />
                <TableRow
                  leftTitle={t('Loan-Purpose-Type')}
                  leftValue={() => (
                    <CopyContentToClipBoard
                      onSuccess={() => setShowToaster(true)}
                      showClipButton={Boolean(
                        loanPurposeTypes(cmhcLowRatioData?.loanPurposeType || '', t)
                      )}
                      clipboardContent={
                        loanPurposeTypes(cmhcLowRatioData?.loanPurposeType || '', t) || ''
                      }
                    >
                      <MemoizedHbtDataTableCell
                        dataValue={String(cmhcLowRatioData?.loanPurposeType)}
                        cellContent={
                          <span>
                            {loanPurposeTypes(cmhcLowRatioData?.loanPurposeType || '', t)}
                          </span>
                        }
                      />
                    </CopyContentToClipBoard>
                  )}
                  rightTitle=""
                  rightValue={() => <></>}
                  className={styles.hideCellBottomBorder}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};
